import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";

/**
 * Thin wrapper over driver.js that adds what our tours need on top of the
 * library defaults:
 *
 *  - per-step `route`: navigate (react-router) before showing the step and
 *    wait for the target to appear (MUI dialogs / lazy pages mount async);
 *  - per-step `advanceWhen`: keep the highlighted element clickable and move
 *    on automatically once that selector appears in the DOM (e.g. "click
 *    UPLOAD CLIPS" → dialog opens → next step). No "Next" needed;
 *  - per-step `optional`: skip silently when the target never shows up
 *    (e.g. the Start button only exists after files were picked);
 *  - "your turn" steps (those with `advanceWhen`) never show a Next that jumps
 *    ahead: the primary button becomes "Do it for me", which performs the
 *    action (opts.onAutoAct(step), falling back to clicking the target) so the
 *    state change advances the tour exactly as a real click would;
 *  - per-step `playground`: a patch handed to `opts.onStep` before the step is
 *    shown — the tutorial popup uses it to put the sandbox into the state the
 *    step talks about (e.g. { network: "offline" });
 *  - per-step `noInteraction`: the highlighted element is shown but cannot be
 *    clicked (e.g. the real Submit button — a tour must never start an upload);
 *  - tutorial-level `endEvent`: window event dispatched when the tour ends,
 *    however it ends (the upload form uses it to clear practice values);
 *  - tutorial-level `endRoute`: where to navigate after the tour is completed;
 *  - per-step `noAutoAct`: a "your turn" step without the Do-it-for-me button
 *    (the user must really do it, e.g. pick their own files);
 *  - one active tour at a time; completion vs. skip reported to the caller.
 *
 * Steps are plain data from registry.js:
 *   { target: '[data-tour="x"]', title, description, route?, advanceWhen?, optional?,
 *     playground?, side?, align? }
 */

const WAIT_FOR_TARGET_MS = 2500;
const POLL_MS = 100;
// Layout under a highlighted element can change while a step is open (the
// playground card grows/shrinks, a dialog animates in). driver.js only
// re-measures on window resize, so we nudge it periodically.
const REFRESH_MS = 300;

// { instance, finish } of the running tour, or null.
let activeTour = null;

const currentLocation = () =>
  `${window.location.pathname}${window.location.search || ""}`;

const waitForSelector = (selector, timeoutMs = WAIT_FOR_TARGET_MS) =>
  new Promise((resolve) => {
    if (!selector) return resolve(null);
    const found = document.querySelector(selector);
    if (found) return resolve(found);
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const el = document.querySelector(selector);
      if (el || Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        resolve(el || null);
      }
    }, POLL_MS);
  });

export const isTourActive = () => !!activeTour;

export const stopActiveTour = () => {
  if (activeTour) {
    const { instance, finish } = activeTour;
    try {
      instance.destroy();
    } catch {
      /* already gone */
    }
    // driver.js only fires onDestroyed when its internal active-element state
    // was set (rAF-dependent); settle ourselves so callers always resolve.
    finish("skipped");
  }
};

/**
 * @param {object} tutorial registry entry
 * @param {object} opts
 * @param {(to: string) => void} opts.navigate  react-router navigate
 * @param {() => void} [opts.onComplete]
 * @param {() => void} [opts.onSkip]
 * @param {(step: object, index: number) => void} [opts.onStep]  called before each step is shown
 * @param {(step: object, index: number) => boolean|void} [opts.onAutoAct] "Do it for me" handler; return true when handled
 * @returns {Promise<"completed"|"skipped">}
 */
export const startTour = (tutorial, { navigate, onComplete, onSkip, onStep, onAutoAct } = {}) => {
  stopActiveTour();

  // Optional tutorial-level scope: playground tours must not match the static
  // previews (same data-tour anchors) sitting on the overview page behind the
  // dialog, so every selector is prefixed with the sandbox root.
  const scope = tutorial?.scope ? `${tutorial.scope} ` : "";
  const scoped = (selector) => (selector ? `${scope}${selector}` : selector);
  const steps = Array.isArray(tutorial?.steps)
    ? tutorial.steps.map((step) => ({
        ...step,
        target: scoped(step.target),
        advanceWhen: scoped(step.advanceWhen),
      }))
    : [];
  if (steps.length === 0) {
    onSkip?.();
    return Promise.resolve("skipped");
  }

  let completed = false;
  let advanceObserver = null;
  let advanceTimer = null;
  let refreshTimer = null;
  let disposed = false;

  const clearAdvanceWatch = () => {
    document.querySelectorAll(".clipn-tour-yourturn-target").forEach((n) => n.classList.remove("clipn-tour-yourturn-target"));
    if (advanceObserver) {
      advanceObserver.disconnect();
      advanceObserver = null;
    }
    if (advanceTimer) {
      window.clearInterval(advanceTimer);
      advanceTimer = null;
    }
  };

  const goTo = async (route) => {
    if (!route) return;
    if (currentLocation() === route) return;
    if (typeof navigate === "function") {
      navigate(route);
    } else {
      window.history.pushState({}, "", route);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  // Resolve which concrete step index to show next, honouring `route` and
  // `optional`. Returns the index or -1 when we ran off the end.
  const prepare = async (fromIndex, direction) => {
    let index = fromIndex;
    while (index >= 0 && index < steps.length) {
      const step = steps[index];
      await goTo(step.route);
      try {
        onStep?.(step, index);
      } catch {
        /* a broken hook must not kill the tour */
      }
      const el = await waitForSelector(step.target, step.waitMs || WAIT_FOR_TARGET_MS);
      if (el || !step.optional) return index;
      index += direction;
    }
    return -1;
  };

  return new Promise((resolve) => {
    const finish = (outcome) => {
      if (disposed) return;
      disposed = true;
      clearAdvanceWatch();
      if (refreshTimer) {
        window.clearInterval(refreshTimer);
        refreshTimer = null;
      }
      if (activeTour && activeTour.instance === instance) activeTour = null;
      if (tutorial?.endEvent) {
        try {
          window.dispatchEvent(new CustomEvent(tutorial.endEvent, { detail: { outcome } }));
        } catch {
          /* ignore */
        }
      }
      if (outcome === "completed") onComplete?.();
      else onSkip?.();
      if (outcome === "completed" && tutorial?.endRoute && typeof navigate === "function") {
        try {
          navigate(tutorial.endRoute);
        } catch {
          /* ignore */
        }
      }
      resolve(outcome);
    };

    // Arm the "your turn" watcher for a step: pulse its target and advance as
    // soon as `advanceWhen` matches. Called by us after every step change (not
    // only from driver's onHighlighted, which can be skipped when React
    // re-renders the highlighted node mid-transition).
    let armedIndex = -1;
    const armStep = (index, { fromBack = false } = {}) => {
      clearAdvanceWatch();
      armedIndex = index;
      const step = steps[index];
      if (!step?.advanceWhen) return;
      if (fromBack && document.querySelector(step.advanceWhen)) {
        // Came back to a step whose action is already done: nothing to wait
        // for, so don't lock Next and don't auto-jump forward again.
        const cfgStep = instance.getConfig?.().steps?.[index];
        if (cfgStep?.popover) cfgStep.popover.disableButtons = [];
        return;
      }
      const check = () => {
        if (disposed || armedIndex !== index) return;
        if (document.querySelector(step.advanceWhen)) {
          clearAdvanceWatch();
          moveForward(index);
        }
      };
      advanceObserver = new MutationObserver(check);
      advanceObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
      // Belt and braces for portals that mutate outside body observation.
      advanceTimer = window.setInterval(check, 250);
      check();
    };

    // "Do it for me": perform the step's action; the advanceWhen watcher moves on.
    const autoAct = (index) => {
      const step = steps[index];
      if (!step?.advanceWhen || disposed) return;
      let handled = false;
      try {
        handled = onAutoAct?.(step, index) === true;
      } catch {
        handled = false;
      }
      if (!handled && step.event) {
        // App-tour steps can name a window event the real screen listens to
        // (e.g. MetadataForm fills practice values on "clipn:tutorial-fill-metadata").
        window.dispatchEvent(new CustomEvent(step.event));
        handled = true;
      }
      if (!handled) {
        // Fallback: click the target itself, or the first clickable inside it
        // (wrappers like the state-control span hold the real button).
        const target = document.querySelector(step.target);
        const clickable =
          target && !target.matches("button, a, input, [role='button']")
            ? target.querySelector("button, a, input, [role='button']") || target
            : target;
        if (clickable && typeof clickable.click === "function") clickable.click();
      }
    };

    // driver renders one popover + one overlay; if a stale pair survives a
    // superseded transition (seen as a "ghost" popover), drop all but the last.
    const dedupeDriverDom = () => {
      const popovers = document.querySelectorAll(".driver-popover");
      for (let i = 0; i < popovers.length - 1; i += 1) popovers[i].remove();
      const overlays = document.querySelectorAll(".driver-overlay");
      for (let i = 0; i < overlays.length - 1; i += 1) overlays[i].remove();
    };

    // React owns the class attribute of the highlighted node and rewrites it on
    // re-render (e.g. a toggle gaining Mui-selected), dropping the classes driver
    // relies on for pointer-events. Re-assert them while a step is open.
    const reassertActiveClasses = () => {
      if (disposed || armedIndex < 0) return;
      const step = steps[armedIndex];
      if (!step) return;
      const el = document.querySelector(step.target);
      if (!el) return;
      // Only ONE element may look highlighted: React re-renders make driver
      // lose track of nodes it marked, so drop the class from every element
      // that is not the current step's target before re-asserting it.
      document.querySelectorAll(".driver-active-element").forEach((node) => {
        if (node !== el) node.classList.remove("driver-active-element", "clipn-tour-yourturn-target");
      });
      if (!el.classList.contains("driver-active-element")) el.classList.add("driver-active-element");
      if (step.advanceWhen && !el.classList.contains("clipn-tour-yourturn-target")) {
        el.classList.add("clipn-tour-yourturn-target");
      }
    };


    const instance = driver({
      // No stage animation: while driver animates between steps the next
      // target isn't clickable yet, and users click the moment they read
      // "click X". Instant transitions keep those clicks from being swallowed.
      animate: false,
      showProgress: steps.length > 1,
      progressText: "{{current}} of {{total}}",
      // A stray click on the dim overlay must not end the tour (that's the
      // easiest way to lose a 14-step walkthrough). The ✕ and Esc still do.
      allowClose: true,
      overlayClickBehavior: () => {},
      allowKeyboardControl: true,
      // The sandbox card must stay readable while a step talks about a status
      // that is NOT the highlighted element, so the dim is deliberately light.
      overlayOpacity: 0.45,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: "clipn-tour-popover",
      disableActiveInteraction: false,
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      steps: steps.map((s) => ({
        element: s.target,
        ...(s.noInteraction ? { disableActiveInteraction: true } : {}),
        popover: {
          title: s.title,
          description: s.advanceWhen
            ? `${s.description}<div class="clipn-tour-yourturn">👉 <strong>Your turn</strong> — Next unlocks when it's done.${
                s.noAutoAct ? "" : " Stuck? Press <em>Do it for me</em>."
              } ✕ leaves the tour.</div>`
            : s.description,
          side: s.side || "bottom",
          align: s.align || "start",
          ...(s.advanceWhen
            ? { disableButtons: ["next"], popoverClass: "clipn-tour-popover clipn-tour-yourturn-step" }
            : {}),
        },
      })),
      onHighlighted: (_el, _step, { index }) => {
        if (index !== armedIndex) armStep(index);
      },
      onNextClick: (_el, _step, { index }) => {
        const step = steps[index];
        // "Your turn" steps: Next is disabled unless the action is already
        // done (e.g. revisited via Back) — then Next simply moves on.
        if (step?.advanceWhen && !document.querySelector(step.advanceWhen)) return;
        moveForward(index);
      },
      onPopoverRender: (popover, { index }) => {
        const step = steps[index];
        if (!step?.advanceWhen || step.noAutoAct || !popover?.footerButtons) return;
        // Extra "Do it for me" button next to the (disabled) Next.
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "driver-popover-footer-btn clipn-tour-autoact-btn";
        btn.textContent = "Do it for me";
        btn.addEventListener("click", () => autoAct(index));
        popover.footerButtons.insertBefore(btn, popover.nextButton || null);
      },
      onPrevClick: async (_el, _step, { index }) => {
        const target = await prepare(index - 1, -1);
        if (disposed || target < 0) return;
        armStep(target, { fromBack: true }); // before moveTo so the popover renders with the right buttons
        instance.moveTo(target);
      },
      onDestroyStarted: () => {
        // User pressed Esc / clicked ✕: always a skip, even on the last step.
        // "Completed" is reserved for Done, because completion has side
        // effects (endRoute navigation, the upload form closing).
        instance.destroy();
        finish("skipped");
      },
      onDestroyed: () => {
        finish(completed ? "completed" : "skipped");
      },
    });

    const moveForward = async (index) => {
      if (disposed) return;
      if (index >= steps.length - 1) {
        completed = true;
        instance.destroy();
        finish("completed");
        return;
      }
      const target = await prepare(index + 1, +1);
      if (disposed) return;
      if (target < 0) {
        completed = true;
        instance.destroy();
        finish("completed");
        return;
      }
      instance.moveTo(target);
      armStep(target);
    };

    activeTour = { instance, finish };

    prepare(0, +1).then((first) => {
      if (disposed) return;
      if (first < 0) {
        finish("skipped");
        return;
      }
      instance.drive(first);
      armStep(first);
      refreshTimer = window.setInterval(() => {
        try {
          reassertActiveClasses();
          dedupeDriverDom();
          if (instance.isActive()) instance.refresh();
        } catch {
          /* ignore */
        }
      }, REFRESH_MS);
    });
  });
};
